/**
 * basement-cms — resolución RUNTIME de los feature flags PÚBLICOS del storefront.
 *
 * Reemplaza el `environment.featuredOffersEnabled` (build-time) por el mapa
 * `mod.cms.storefront.*` que Foundation resuelve **per-org en runtime**
 * (`GET /cms/delivery/features`, PR backend #250). Así el CEO prende/apaga el
 * flag desde el panel admin, **sin redeploy y al instante** (respuesta
 * `Cache-Control: no-store`).
 *
 * Mecanismo (PLAN-foundation-feature-flags §5.5/§5.6, decisiones D-7/D-8):
 * - Se carga UNA vez vía `provideAppInitializer` ANTES del primer componente
 *   ruteado (`storefront-features.provider.ts`). Cuando la factory del token
 *   `FEATURED_OFFERS_ENABLED` corre, el snapshot ya está poblado → el token
 *   sigue siendo `boolean` puro y el call-site NO cambia.
 * - **SSR**: fetch al BFF con la `X-Delivery-Key` server-only y embebe el mapa
 *   en un `TransferState` PROPIO.
 * - El fetch está acotado por `READ_TIMEOUT_MS` en AMBAS plataformas: corre en
 *   un `provideAppInitializer` que bloquea el bootstrap, así que un upstream
 *   colgado nunca puede dejar la app sin arrancar (cae al fallback D-7).
 * - **Browser**: lee ese `TransferState` (SIN removerlo — NO es el one-shot de
 *   `DeliveryTransferCache`; es estado de vida-de-la-app) → resuelve síncrono,
 *   cero red. Sólo si falta (nav SPA, SSR falló) hace 1 fetch por el proxy.
 * - **DECISIÓN ≠ FALLO (regla del CEO, 2026-07-23 — reemplaza al fallback D-7)**:
 *   lo que dice el endpoint es una DECISIÓN y manda siempre, en ON o en OFF.
 *   Un timeout/red/5xx/envelope-roto es un FALLO, y no puede hacerse pasar por
 *   una decisión de apagar. Ante fallo, cada feature aplica su
 *   {@link BackendFailurePolicy}: las **capacidades del plan** (la tienda)
 *   mantienen el **último estado conocido**; las **opcionales** (carrusel) caen
 *   a **OFF**. Todo con telemetría (`stale_used` / `fail_closed`), nunca en
 *   silencio. El initializer sigue sin rechazar jamás.
 *   El D-7 original caía al default de `environment.*` para todo, o sea que un
 *   backend caído podía REABRIR una feature que el CEO había apagado.
 *
 * Relación con ADR-FE-001: un flag es *configuración de qué vista montar*, no
 * *datos de la vista*. Fail-closed resuelve a un valor determinista (OFF) y
 * grita en telemetría; no pinta un empty-state mudo. Lo que muestre el bloque
 * una vez montado sí lo gobierna `RequestState<T>` (p. ej. el offers-carousel).
 */
import {
  inject,
  Injectable,
  isDevMode,
  makeStateKey,
  PLATFORM_ID,
  REQUEST,
  StateKey,
  TransferState,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, TimeoutError } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { Meta } from '@angular/platform-browser';
import { SKIP_AUTH_LOGOUT } from '@core/interceptors/auth.interceptor';
import { CMS_DELIVERY_BASE_URL, CMS_DELIVERY_KEY, DELIVERY_PROXY_PATH } from '@core/cms-sdk';
import {
  CMS_FLAGS_SOURCE_META,
  FlagsResolutionSource,
} from '@domains/storefront/shared/flags-source';

/**
 * Presupuesto (ms) del fetch de features. Se aplica en AMBAS plataformas —
 * NO sólo en SSR: este fetch corre dentro de `provideAppInitializer`, que
 * BLOQUEA el bootstrap hasta que resuelve. A diferencia de las lecturas de
 * Delivery (`HttpCmsDeliveryApi`, que corren DURANTE el render y el usuario
 * puede reintentar), un `/delivery-proxy/features` colgado en el browser
 * dejaría la app sin arrancar para siempre y jamás caería al fallback. Acotarlo
 * convierte el cuelgue en `TimeoutError` ⇒ fallback a `environment.*` (D-7).
 */
const READ_TIMEOUT_MS = 4000;

/** Prefijo público: sólo estas claves salen del endpoint (allow-list del BFF). */
const STOREFRONT_PREFIX = 'mod.cms.storefront.';

/**
 * Clave RAÍZ del subárbol público (#598). NO matchea `STOREFRONT_PREFIX`, que
 * exige el punto final — por eso se acepta aparte: es el flag que gobierna la
 * existencia de TODA la superficie de tienda, y filtrarlo dejaba al FE ciego
 * justo al flag que el CEO apaga desde el panel.
 */
export const STOREFRONT_ROOT = 'mod.cms.storefront';

/** Clave del `TransferState` propio (NO se remueve al leer, a diferencia del one-shot). */
const FEATURES_STATE_KEY: StateKey<FeaturesState> = makeStateKey<FeaturesState>('cms-features');

/** Mapa plano `featureId → habilitado` que expone el endpoint público. */
export type StorefrontFeatureMap = Readonly<Record<string, boolean>>;

/**
 * De dónde salió el snapshot vigente. Es la pieza que permite **distinguir una
 * DECISIÓN del CEO de un FALLO del backend**, que antes se trataban igual:
 *
 * - `fresh` — el endpoint respondió. Lo que dice es la decisión del CEO, sea ON
 *   u OFF, y manda para todas las features.
 * - `last-known-good` — el endpoint falló y se está sirviendo el último efectivo
 *   que SÍ se pudo resolver. No es una decisión: es la mejor evidencia
 *   disponible de cuál era la decisión.
 * - `none` — falló y no hay nada recordado (arranque en frío durante la caída).
 *   No hay evidencia de ninguna clase.
 */
type ResolutionSource = FlagsResolutionSource;

/**
 * Estado que el SSR embebe para que el browser reproduzca EXACTAMENTE su
 * resolución (invariante de hydration): `features` es el mapa vigente y `source`
 * de dónde salió. Sin `source`, un SSR degradado haría que el browser re-fetchee
 * y resuelva un valor distinto al del HTML → mismatch de hydration; y peor, el
 * browser no podría saber que ese mapa es stale y trataría una feature opcional
 * recordada como si estuviera confirmada.
 */
interface FeaturesState {
  readonly features: StorefrontFeatureMap;
  readonly source: ResolutionSource;
}

/**
 * ÚLTIMO ESTADO CONOCIDO, keyeado por **identidad del sitio** (el host del
 * pedido en SSR). Vive a nivel de MÓDULO, no de instancia, a propósito:
 *
 * - en **SSR** el injector es por request, así que el estado de instancia se
 *   perdería entre pedidos; a nivel de módulo sobrevive lo que vive el proceso
 *   Node, que es justo lo que hace falta para que un 5xx puntual no cierre una
 *   tienda que el proceso ya había resuelto como habilitada;
 * - en **browser** sobrevive a las navegaciones SPA de la sesión.
 *
 * Sin TTL a propósito: es "el último valor que pudimos confirmar", y recortarlo
 * por tiempo convertiría una caída larga en el outage que se quiere evitar. El
 * costo, explícito: una decisión de apagar tomada DURANTE una caída no se aplica
 * hasta que el endpoint vuelva — no hay forma de enterarse sin el endpoint.
 *
 * AISLAMIENTO ENTRE ORGS: los flags son per-org, así que una cache de proceso
 * compartida sin clave verificable podría servirle a una org el estado recordado
 * de OTRA durante una caída — y abrirle o cerrarle la tienda por error. Por eso
 * la clave sale del **host del pedido** (`REQUEST`), no del origen del BFF, que
 * puede ser el mismo para muchas orgs. Y si el host no se puede determinar, NO
 * se recuerda ni se lee nada (ver `memoryKey`): sin identidad verificable es
 * preferible perder la memoria que mezclar tenants.
 */
const lastKnownGood = new Map<string, StorefrontFeatureMap>();

/**
 * Borra la memoria del último estado conocido.
 *
 * La usan los tests para aislarse entre sí — la cache vive a nivel de módulo, así
 * que sin esto un test que resuelve OK le filtraría su valor al siguiente que
 * simula una caída, y el test de "fallo sin nada recordado" pasaría por el
 * camino equivocado.
 *
 * También es el punto de invalidación si alguna vez un mismo proceso llegara a
 * servir más de una org por el mismo origen de Delivery.
 * @returns void
 */
export function clearStorefrontFeatureMemory(): void {
  lastKnownGood.clear();
}

/**
 * Política ante un **FALLO del backend** (no ante una decisión del CEO, que
 * siempre manda). La elige cada call-site según la clase de la feature:
 *
 * - `'off'` — **features OPCIONALES** (carrusel, módulos accesorios). Si no se
 *   sabe, se apaga: no pasa nada grave.
 * - `'last-known-good'` — **capacidades del plan** (la tienda: catálogo, carrito,
 *   checkout). Un hipo del backend NO puede cerrarle la tienda a una org que la
 *   tiene contratada, así que se sirve el último efectivo conocido.
 *
 * Es un parámetro REQUERIDO: obliga a cada call-site a declarar de qué clase es
 * su feature, en vez de heredar una política por descuido.
 */
export type BackendFailurePolicy = 'off' | 'last-known-good';

/** Envelope estándar del BFF para `GET /cms/delivery/features`. */
interface FeaturesEnvelope {
  readonly data?: { readonly features?: StorefrontFeatureMap };
}

/** Motivo del fallback, para la telemetría (`feature_flags.fallback_used`). */
type FallbackReason = 'timeout' | 'network' | 'http_5xx' | 'malformed' | 'premature_read';

/** Envelope con forma inesperada (dispara el fallback con `reason:"malformed"`). */
class MalformedFeaturesError extends Error {}

@Injectable({ providedIn: 'root' })
export class StorefrontFeatureService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly meta = inject(Meta);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  /** Same-origin: en browser TODA lectura sale por el proxy (la base absoluta sólo rige en SSR). */
  private readonly base = this.isServer ? inject(CMS_DELIVERY_BASE_URL) : DELIVERY_PROXY_PATH;

  /** La key viaja SOLO desde el server SSR; en browser se fuerza vacía (el proxy la inyecta). */
  private readonly deliveryKey = this.isServer ? inject(CMS_DELIVERY_KEY) : '';

  /** Pedido SSR en curso; `null` en browser (y en SSR sin request disponible). */
  private readonly request = inject(REQUEST, { optional: true });

  /**
   * Clave de la memoria del último estado conocido, o `null` para NO usarla.
   *
   * - **SSR** ⇒ host del pedido. Es lo que identifica al sitio/org: el origen del
   *   BFF puede ser compartido por muchas orgs, así que keyear por ahí mezclaría
   *   tenants bajo una caída.
   * - **SSR sin host determinable** ⇒ `null`: sin identidad verificable no se
   *   recuerda ni se lee. Se pierde la memoria (la tienda cae al default del
   *   deployment), que es mucho menos grave que servirle a una org la decisión
   *   de otra.
   * - **Browser** ⇒ una instancia de la app sirve UN sitio, así que alcanza el
   *   origen de lectura.
   */
  private readonly memoryKey: string | null = this.resolveMemoryKey();

  private snapshot: StorefrontFeatureMap = {};
  private resolutionSource: ResolutionSource = 'none';
  private isReady = false;

  /** `true` una vez que `load()` completó (incluso por la rama de fallback). */
  get ready(): boolean {
    return this.isReady;
  }

  /**
   * `true` si el snapshot viene de una resolución FRESCA del backend, o sea que
   * refleja la decisión actual del CEO y no una copia recordada.
   */
  get resolved(): boolean {
    return this.isReady && this.resolutionSource === 'fresh';
  }

  /** De dónde salió el snapshot vigente (para telemetría y tests). */
  get source(): ResolutionSource {
    return this.resolutionSource;
  }

  /**
   * Carga el mapa de features público. Idempotente-por-bootstrap: la corre el
   * `provideAppInitializer`. NUNCA rechaza (un flag no puede tumbar la home).
   * @returns Promesa que resuelve cuando el snapshot quedó disponible.
   */
  async load(): Promise<void> {
    const embedded = this.readTransferState();
    if (embedded) {
      this.snapshot = embedded.features;
      this.resolutionSource = embedded.source;
      // Un mapa FRESCO embebido por el SSR también sirve de último-estado-conocido
      // para las navegaciones SPA posteriores de esta sesión.
      if (embedded.source === 'fresh' && this.memoryKey) {
        lastKnownGood.set(this.memoryKey, embedded.features);
      }
      this.isReady = true;
      return;
    }

    const startedAt = Date.now();
    try {
      this.snapshot = await this.fetchFeatures();
      this.resolutionSource = 'fresh';
      if (this.memoryKey) {
        lastKnownGood.set(this.memoryKey, this.snapshot);
      }
    } catch (err) {
      this.emitFallback(this.reasonOf(err), Date.now() - startedAt);
      // El backend falló: eso NO es una decisión de apagar. Se sirve el último
      // efectivo confirmado, si lo hay; el que decide qué hacer con un mapa stale
      // es cada call-site, según la clase de su feature (`BackendFailurePolicy`).
      const remembered = this.memoryKey ? lastKnownGood.get(this.memoryKey) : undefined;
      if (remembered) {
        this.snapshot = remembered;
        this.resolutionSource = 'last-known-good';
        this.emitStaleUsed();
      } else {
        this.snapshot = {};
        this.resolutionSource = 'none';
      }
    } finally {
      // El SSR embebe SIEMPRE su resolución —fresca o degradada— para que el
      // browser la reproduzca sin red y sin romper hydration. `source` viaja con
      // el mapa: sin él, el browser trataría un valor recordado como confirmado y
      // dejaría prendida una feature opcional que debía apagarse.
      if (this.isServer) {
        this.transferState.set(FEATURES_STATE_KEY, {
          features: this.snapshot,
          source: this.resolutionSource,
        });
        // …y marca el documento para que `server.ts` lo traduzca al header
        // `x-flags-source` (#636). El TransferState NO alcanza: el edge no puede
        // depender de la forma interna de un TransferState de Angular — ya se
        // intentó y se pudrió en silencio (ver `shared/flags-source.ts`).
        // Va acá, en el `finally`, porque es el único punto que corre en las
        // TRES ramas (fresh, last-known-good y none) y porque `load()` la
        // ejecuta desde un `provideAppInitializer`, que BLOQUEA el bootstrap:
        // cuando esto corre, todavía no existe el primer componente ruteado, o
        // sea que el meta entra al `<head>` muy antes de la serialización.
        this.meta.updateTag({ name: CMS_FLAGS_SOURCE_META, content: this.resolutionSource });
      }
      this.isReady = true;
    }
  }

  /**
   * Resuelve un flag público per-org, distinguiendo **decisión** de **fallo**.
   *
   * Ésta es la regla, tal como la fijó el CEO (2026-07-23):
   *
   * | Situación | Qué es | Resultado |
   * |---|---|---|
   * | El endpoint responde OFF | **decisión** deliberada | OFF, siempre. Apagar es apagar. |
   * | El endpoint responde ON | **decisión** deliberada | ON. |
   * | El endpoint falla, hay último estado conocido | **fallo** | `'last-known-good'` ⇒ ese valor · `'off'` ⇒ OFF |
   * | El endpoint falla, no hay nada recordado | **fallo** sin evidencia | OFF (el call-site puede preferir su default; ver {@link hasValueFor}) |
   * | Lectura antes del bootstrap | bug de wiring | OFF + `premature_read` ruidoso |
   *
   * El bug que cierra: antes un 5xx y un "el CEO lo apagó" producían el MISMO
   * resultado, así que un hipo del backend podía dejar sin tienda a una org que
   * la tiene contratada. Ahora un fallo nunca se confunde con una decisión: las
   * capacidades del plan mantienen el último estado conocido y sólo las features
   * opcionales caen a OFF.
   *
   * No hay parámetro `envDefault` a propósito: la política ante fallo se declara
   * con un valor tipado (`onBackendFailure`), no con un booleano suelto que un
   * call-site pudiera usar para reabrir el fallback sin que se note en revisión.
   * @param featureId Clase completa, p. ej. `mod.cms.storefront.offers`.
   * @param onBackendFailure Clase de la feature: `'off'` para opcionales,
   * `'last-known-good'` para capacidades del plan.
   * @returns Si la feature está habilitada para la org actual.
   */
  isEnabled(featureId: string, onBackendFailure: BackendFailurePolicy): boolean {
    if (!this.isReady) {
      this.emitPrematureRead(featureId);
      return false;
    }
    // Decisión del backend: manda, esté en ON o en OFF.
    if (this.resolutionSource === 'fresh') {
      return this.snapshot[featureId] ?? false;
    }
    // A partir de acá hubo un FALLO, no una decisión.
    if (onBackendFailure === 'off') {
      this.emitFailClosed(featureId);
      return false;
    }
    return this.snapshot[featureId] ?? false;
  }

  /**
   * Si hay **algún** valor para la clave — fresco del endpoint o recordado del
   * último estado conocido. Antes se llamaba `isPublished`, pero el nombre ya no
   * alcanzaba: ahora también cuenta lo recordado.
   *
   * Distingue "el CEO apagó esto" de "no tenemos ninguna afirmación sobre esta
   * clave". Lo segundo pasa en dos casos, y en ninguno hay algo apagado que
   * respetar:
   *  - el allow-list del BFF no expone la clave (#598: hoy prod sirve
   *    `mod.cms.storefront.offers` pero NO su padre `mod.cms.storefront`, porque
   *    filtra por el prefijo CON punto);
   *  - el endpoint falló y no había nada recordado (arranque en frío durante una
   *    caída).
   *
   * Un gate que cerrara por cualquiera de esos dos apagaría algo que NADIE apagó
   * — en el primer caso, la tienda entera en producción hoy mismo.
   * @param featureId Clave completa, p. ej. `mod.cms.storefront`.
   * @returns `true` si hay un valor (fresco o recordado) para esa clave.
   */
  hasValueFor(featureId: string): boolean {
    return this.isReady && this.resolutionSource !== 'none' && featureId in this.snapshot;
  }

  /**
   * Calcula la clave de aislamiento de la memoria (ver {@link memoryKey}).
   * @returns La clave, o `null` si no hay identidad verificable.
   */
  private resolveMemoryKey(): string | null {
    if (!this.isServer) {
      return `browser:${this.base}`;
    }
    const url = this.request?.url;
    if (!url) {
      return null;
    }
    try {
      return `server:${new URL(url).host}`;
    } catch {
      return null;
    }
  }

  /**
   * Lee el estado embebido por el SSR sin consumirlo (vive lo que vive la app).
   * @returns El estado embebido, o `null` en SSR / browser sin transfer state.
   */
  private readTransferState(): FeaturesState | null {
    if (this.isServer || !this.transferState.hasKey(FEATURES_STATE_KEY)) {
      return null;
    }
    return this.transferState.get(FEATURES_STATE_KEY, { features: {}, source: 'none' });
  }

  /**
   * Fetch real del endpoint público, filtrado al prefijo del storefront.
   * @returns El mapa `mod.cms.storefront.*` resuelto para la org.
   */
  private async fetchFeatures(): Promise<StorefrontFeatureMap> {
    const options = {
      ...(this.deliveryKey ? { headers: { 'X-Delivery-Key': this.deliveryKey } } : {}),
      context: new HttpContext().set(SKIP_AUTH_LOGOUT, true),
    };
    const req$ = this.http
      .get<FeaturesEnvelope>(`${this.base}/features`, options)
      .pipe(
        // Acotado en ambas plataformas: el fetch bloquea el bootstrap (corre en
        // el app-initializer), así que un upstream colgado NO puede quedar sin
        // timeout ni en browser (si no, la app no arranca y no cae al fallback).
        timeout(READ_TIMEOUT_MS),
        map((res) => this.parse(res)),
      );
    return firstValueFrom(req$);
  }

  /**
   * Valida el envelope y aísla las claves del prefijo público (defensa en
   * profundidad: aunque el BFF ya filtra, el storefront jamás ve otra topología).
   * @param res Envelope del BFF.
   * @returns El mapa filtrado.
   */
  private parse(res: FeaturesEnvelope): StorefrontFeatureMap {
    const features = res?.data?.features;
    if (!features || typeof features !== 'object') {
      throw new MalformedFeaturesError('features envelope missing data.features');
    }
    const out: Record<string, boolean> = {};
    for (const [id, enabled] of Object.entries(features)) {
      const isPublicKey = id === STOREFRONT_ROOT || id.startsWith(STOREFRONT_PREFIX);
      if (isPublicKey && typeof enabled === 'boolean') {
        out[id] = enabled;
      }
    }
    return out;
  }

  /**
   * Clasifica el error de resolución para la telemetría.
   * @param err Error capturado en `load()`.
   * @returns El motivo normalizado.
   */
  private reasonOf(err: unknown): FallbackReason {
    if (err instanceof TimeoutError) {
      return 'timeout';
    }
    if (err instanceof MalformedFeaturesError) {
      return 'malformed';
    }
    if (err instanceof HttpErrorResponse) {
      return err.status >= 500 ? 'http_5xx' : 'network';
    }
    return 'network';
  }

  /**
   * Emite `feature_flags.fallback_used` (warn) — el fallback NUNCA es silencioso.
   * @param reason Motivo del fallback.
   * @param durationMs Ms hasta la falla (distingue timeout de rechazo inmediato).
   */
  private emitFallback(reason: FallbackReason, durationMs: number): void {
    this.emit('feature_flags.fallback_used', {
      reason,
      durationMs,
      flagsAffected: [`${STOREFRONT_PREFIX}*`],
    });
  }

  /**
   * Emite `feature_flags.stale_used` (warn) — se está sirviendo el último estado
   * conocido porque el backend falló. Es lo que permite ver, desde afuera, que
   * una tienda sigue abierta por memoria y no por confirmación.
   */
  private emitStaleUsed(): void {
    this.emit('feature_flags.stale_used', {
      reason: 'backend_unavailable',
      flagsAffected: [`${STOREFRONT_PREFIX}*`],
    });
  }

  /**
   * Emite `feature_flags.fail_closed` (warn) — se resolvió OFF por no poder
   * confirmar el flag, no porque el panel lo haya apagado.
   *
   * Sólo aplica a features OPCIONALES (`onBackendFailure: 'off'`). Distinguirlo
   * en telemetría importa: "la feature está apagada" y "la feature se apagó sola
   * porque el backend se cayó" se ven idénticas desde la UI, y sólo la segunda
   * es un incidente.
   * @param featureId Clave resuelta en falso por fail-closed.
   */
  private emitFailClosed(featureId: string): void {
    this.emit('feature_flags.fail_closed', { reason: 'unconfirmed', featureId });
  }

  /**
   * Emite `feature_flags.premature_read` (warn) — el token se inyectó antes de
   * que la resolución termine (bug de wiring en el bootstrap).
   * @param featureId Clave leída prematuramente.
   */
  private emitPrematureRead(featureId: string): void {
    this.emit('feature_flags.premature_read', { reason: 'premature_read', featureId });
  }

  /**
   * Log estructurado JSON: en SSR lo levanta Loki; en browser va a la consola.
   * @param event Nombre del evento.
   * @param fields Campos adicionales del evento.
   */
  private emit(event: string, fields: Record<string, unknown>): void {
    const platform = this.isServer ? 'server' : 'browser';
    // eslint-disable-next-line no-console -- telemetría deliberada (D-7): nunca silenciosa.
    console.warn(JSON.stringify({ event, level: 'warn', platform, ...fields }));
    if (isDevMode()) {
      // eslint-disable-next-line no-console -- ruido intencional en dev para bugs de wiring.
      console.error(`[storefront-features] ${event}`, fields);
    }
  }
}
